/**
 * Outfitter View
 * Shop where players BUY gear, tackle, and equipment
 * Organized by zone for clarity
 */

import { useState, useMemo } from 'react';
import { useGameStore } from '@/state';
import { outfitterManager } from '@/systems';
import { OutfitterDefinition } from '@/types';
import { formatCurrency } from '@/data';
import { ZONE_DEFINITIONS } from '@/data/zones/zoneData';
import { ScrollableView } from '@/components/layout/ScrollableView';
import { BackButton } from '@/components/layout/BackButton';
import { EmptyState } from '@/components/layout/EmptyState';
import { DetailView } from '@/components/layout/DetailView';
import { EquipmentShopView } from '@/components/shops/EquipmentShopView';

interface OutfitterCardProps {
  outfitter: OutfitterDefinition;
  onVisit: (outfitterId: string) => void;
}

function OutfitterCard({ outfitter, onVisit }: OutfitterCardProps) {
  const isUnlocked = outfitterManager.isOutfitterUnlocked(outfitter.id);
  const canUnlock = outfitterManager.canUnlockOutfitter(outfitter.id);

  const handleVisit = () => {
    outfitterManager.visitOutfitter(outfitter.id);
    onVisit(outfitter.id);
  };

  const handleUnlock = () => {
    outfitterManager.unlockOutfitter(outfitter.id);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 border-2 border-gray-700">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white">{outfitter.name}</h3>
          <p className="text-gray-400 text-sm">{outfitter.description}</p>
          {outfitter.npcName && (
            <p className="text-gray-500 text-xs mt-1">Owner: {outfitter.npcName}</p>
          )}
        </div>
        <div className="text-3xl">🏪</div>
      </div>

      {!isUnlocked ? (
        <div className="mt-4">
          <div className="bg-gray-900 rounded p-3 mb-3">
            <p className="text-sm text-gray-400">🔒 Locked</p>
            {outfitter.unlockRequirements && (
              <div className="text-xs text-gray-500 mt-2">
                {outfitter.unlockRequirements.minCurrency && (
                  <p>Requires: {formatCurrency(outfitter.unlockRequirements.minCurrency)}</p>
                )}
                {outfitter.unlockRequirements.minLevel && (
                  <p>Requires Level: {outfitter.unlockRequirements.minLevel}</p>
                )}
                {outfitter.unlockRequirements.completedQuests && (
                  <p>Complete specific quests</p>
                )}
              </div>
            )}
          </div>
          {canUnlock && (
            <button
              onClick={handleUnlock}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold transition-colors"
            >
              Unlock Shop
            </button>
          )}
        </div>
      ) : (
        <div className="mt-4">
          <div className="bg-gray-900 rounded p-3 mb-3">
            <p className="text-sm text-green-400">✓ Unlocked</p>
            <p className="text-xs text-gray-500 mt-1">
              {outfitter.inventory.length} items available
            </p>
          </div>
          <button
            onClick={handleVisit}
            className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold transition-colors"
          >
            Visit Shop
          </button>
        </div>
      )}
    </div>
  );
}

export function OutfitterView() {
  const outfitters = outfitterManager.getAllOutfitters();
  const unlockedZones = useGameStore((state) => state.playerState.unlockedZones);

  // Auto-enter the single shop when only 1 is unlocked
  const unlockedOutfitters = useMemo(
    () => outfitters.filter((o) => outfitterManager.isOutfitterUnlocked(o.id)),
    [outfitters]
  );
  const autoEnterId = unlockedOutfitters.length === 1 ? unlockedOutfitters[0].id : null;

  const [visitingOutfitterId, setVisitingOutfitterId] = useState<string | null>(autoEnterId);

  // If visiting an outfitter, show the shop detail view
  if (visitingOutfitterId) {
    const outfitter = outfitterManager.getOutfitter(visitingOutfitterId);

    if (!outfitter) {
      return (
        <ScrollableView>
          <EmptyState emoji="🏪" message="Outfitter not found." />
        </ScrollableView>
      );
    }

    return (
      <DetailView
        toolbar={
          <div className="p-4 border-b border-gray-700">
            <BackButton label="Outfitters" onClick={() => setVisitingOutfitterId(null)} />
          </div>
        }
      >
        {/* Outfitter Detail View */}
        <ScrollableView>
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-start gap-4 mb-6">
              <div className="text-6xl">🏪</div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-2">{outfitter.name}</h1>
                <p className="text-gray-400">{outfitter.description}</p>
                {outfitter.npcName && (
                  <p className="text-gray-500 text-sm mt-1">Owner: {outfitter.npcName}</p>
                )}
              </div>
            </div>

            {/* NPC Dialogue */}
            {outfitter.npcDialogue && outfitter.npcDialogue.length > 0 && (
              <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
                <p className="text-gray-300 italic">
                  "{outfitter.npcDialogue[Math.floor(Math.random() * outfitter.npcDialogue.length)]}"
                </p>
              </div>
            )}

            {/* Equipment Shop */}
            <EquipmentShopView outfitter={outfitter} />
          </div>
        </ScrollableView>
      </DetailView>
    );
  }

  // Group outfitters by zone
  const outfittersByZone = ZONE_DEFINITIONS.map((zone) => {
    const zoneOutfitters = outfitters.filter((o) => o.zoneId === zone.id);
    const unlocked = zoneOutfitters.filter((o) =>
      outfitterManager.isOutfitterUnlocked(o.id)
    );
    const locked = zoneOutfitters.filter(
      (o) => !outfitterManager.isOutfitterUnlocked(o.id)
    );

    return {
      zone,
      outfitters: zoneOutfitters,
      unlocked,
      locked,
      isZoneUnlocked: unlockedZones.includes(zone.id),
    };
  }).filter((group) => group.outfitters.length > 0); // Only show zones with outfitters

  return (
    <ScrollableView gap={8}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">🏪 Outfitters</h1>
        <p className="text-gray-400">
          Buy gear, tackle, boats, and equipment to improve your fishing.
        </p>
      </div>

      {/* Outfitters grouped by zone */}
      {outfittersByZone.map(({ zone, outfitters: _outfitters, unlocked, locked, isZoneUnlocked }) => (
        <div key={zone.id}>
          {/* Zone Header */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-white">{zone.name}</h2>
              {!isZoneUnlocked && (
                <span className="text-xs bg-gray-700 text-gray-400 px-2 py-1 rounded">
                  🔒 Zone Locked
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">{zone.description}</p>
          </div>

          {/* Unlocked Outfitters */}
          {unlocked.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-300 mb-3">Available</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {unlocked.map((outfitter) => (
                  <OutfitterCard
                    key={outfitter.id}
                    outfitter={outfitter}
                    onVisit={setVisitingOutfitterId}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Locked Outfitters */}
          {locked.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-300 mb-3">Locked</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {locked.map((outfitter) => (
                  <OutfitterCard
                    key={outfitter.id}
                    outfitter={outfitter}
                    onVisit={setVisitingOutfitterId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {outfitters.length === 0 && (
        <EmptyState emoji="🏪" message="No outfitters available yet." />
      )}
    </ScrollableView>
  );
}
